/**
 * Master WhatsApp Command Center (Phase K).
 * Unified dashboard: metrics + categorized sidebar + chat pane + lead/project intelligence panel + internal notes/mentions.
 * Reuses existing /api/admin/inbox/* endpoints for sending messages, marking read, etc.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import TemplateChooser from "../../components/TemplateChooser";
import OneToOneMarketingCard from "../../components/OneToOneMarketingCard";
import { api } from "../../lib/api";
import { useInboxSocket } from "../../lib/useInboxSocket";
import MessageThread from "../../components/MessageThread";
import AISalesPanel from "../../components/AISalesPanel";
import AIControlHeader from "../../components/AIControlHeader";
import { toast } from "sonner";
import {
  MessageCircle, Search, Filter, X, Send, UserCheck, UserPlus, Flame, Clock,
  CheckCircle, ShieldCheck, Inbox as InboxIcon, Mail, ChevronRight, Loader2,
  History, Briefcase, Layers, IndianRupee, Hash, Users as UsersIcon, AtSign,
  PhoneCall, ListChecks, Star, Activity, Timer, Calendar, RefreshCw, Sparkles,
  AlertTriangle, HeartHandshake, TrendingUp, ArrowLeft, Info,
} from "lucide-react";

// Helper: human-friendly conversation display name
const convDisplayName = (c) =>
  c.title ||
  c.lead?.name ||
  c.whatsapp_phone ||
  c.customer_phone ||
  c.customer_wa_id ||
  "Conversation";

const convDisplaySubtitle = (c) => {
  if (c.subtitle) return c.subtitle;
  const ph = c.whatsapp_phone || c.customer_phone || c.customer_wa_id;
  if (!ph) return "";
  const tail = String(ph).replace(/\D/g, "").slice(-10);
  return tail.length === 10 ? `+91 ${tail.slice(0, 5)} ${tail.slice(5)}` : ph;
};

// A6: Command Center split into Sales / Support / All. Some categories only
// make sense for one channel (a support conversation doesn't have a "hot
// lead" score; a sales conversation has no linked order to be "at risk").
const CHANNELS = [
  { key: "all", label: "All", Icon: InboxIcon },
  { key: "sales", label: "Sales Inbox", Icon: TrendingUp },
  { key: "support", label: "Support Inbox", Icon: HeartHandshake },
];

const BASE_CATEGORIES = [
  { key: "all", label: "All", Icon: InboxIcon },
  { key: "unassigned", label: "Unassigned", Icon: ShieldCheck },
  { key: "assigned_to_me", label: "Assigned to Me", Icon: UserCheck },
];
const SALES_ONLY_CATEGORIES = [
  { key: "hot_leads", label: "Hot Leads", Icon: Flame, badge: "hot_leads" },
  { key: "follow_up_due", label: "Follow-Up Due", Icon: Clock, badge: "follow_ups_due" },
];
const SUPPORT_ONLY_CATEGORIES = [
  { key: "active_order", label: "Active Order", Icon: Briefcase },
  { key: "deadline_at_risk", label: "Deadline at Risk", Icon: AlertTriangle },
  { key: "awaiting_human", label: "Awaiting Human", Icon: UserCheck },
  { key: "sla_breach", label: "SLA Breach", Icon: Flame },
];
const TAIL_CATEGORIES = [
  { key: "clients", label: "Clients", Icon: Briefcase },
  { key: "designers", label: "Designers", Icon: UsersIcon },
  { key: "internal", label: "Internal Team", Icon: AtSign },
  { key: "closed", label: "Closed", Icon: CheckCircle, badge: "closed" },
];

function categoriesForChannel(channel) {
  if (channel === "sales") return [...BASE_CATEGORIES, ...SALES_ONLY_CATEGORIES, ...TAIL_CATEGORIES];
  if (channel === "support") return [...BASE_CATEGORIES, ...SUPPORT_ONLY_CATEGORIES, ...TAIL_CATEGORIES];
  return [...BASE_CATEGORIES, ...SALES_ONLY_CATEGORIES, ...SUPPORT_ONLY_CATEGORIES, ...TAIL_CATEGORIES];
}

const CLASSIFICATION_LABEL = { prospect: "Prospect", customer: "Customer", past_customer: "Past Customer" };
const CLASSIFICATION_COLOR = {
  prospect: "bg-blue-50 text-blue-700 border-blue-200",
  customer: "bg-emerald-50 text-emerald-700 border-emerald-200",
  past_customer: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function CommandCenter() {
  const [searchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";
  const nameFromUrl = searchParams.get("name") || "";
  const [channel, setChannelState] = useState("all");
  const [category, setCategory] = useState("all");
  const setChannel = useCallback((next) => { setChannelState(next); setCategory("all"); }, []);
  const [windowFilter, setWindowFilter] = useState("");
  const [search, setSearch] = useState(searchFromUrl);
  const [convs, setConvs] = useState([]);
  const [convsLoaded, setConvsLoaded] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [team, setTeam] = useState({ admins: [], sales: [], designers: [] });
  const [showAssign, setShowAssign] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatPrefill, setNewChatPrefill] = useState(null); // {phone, name}
  const [showWebhookDebug, setShowWebhookDebug] = useState(false);
  const [showIntelMobile, setShowIntelMobile] = useState(false);
  const [draftInjection, setDraftInjection] = useState(null); // {draft, style, _key, ai_state_intent, ai_state_confidence}
  const injectDraft = useCallback((d) => setDraftInjection({ ...d, _key: Date.now() }), []);
  const { liveConversation, liveMessage, liveTyping, liveAIControl, sendTyping } = useInboxSocket(true);
  const autoNewChatTried = useRef(false);

  const loadConvs = useCallback(() => {
    const q = new URLSearchParams();
    if (channel && channel !== "all") q.set("channel", channel);
    if (category && category !== "all") q.set("category", category);
    if (windowFilter) q.set("filter_window", windowFilter);
    if (search) q.set("search", search);
    api.get(`/admin/command-center/conversations?${q.toString()}`).then(r => { setConvs(r.data || []); setConvsLoaded(true); });
  }, [channel, category, windowFilter, search]);

  const loadMetrics = useCallback(() => {
    api.get("/admin/command-center/metrics").then(r => setMetrics(r.data || {}));
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  // Auto-select first conversation on initial load only (a ref, not state, so this
  // fires once — otherwise the mobile "Back to list" button, which clears activeId
  // to show the list pane, would get instantly overridden right back into the same
  // conversation on every re-render).
  const autoSelectedOnceRef = useRef(false);
  useEffect(() => {
    if (!autoSelectedOnceRef.current && !activeId && convs.length > 0) {
      autoSelectedOnceRef.current = true;
      setActiveId(convs[0].id);
    }
  }, [convs, activeId]);
  // Deep-link from Orders "WA" button: search came in via URL and matched nothing —
  // no existing conversation for this customer, so offer to start one instead of a blank screen.
  useEffect(() => {
    if (!convsLoaded || !searchFromUrl || autoNewChatTried.current) return;
    autoNewChatTried.current = true;
    if (convs.length === 0) {
      setNewChatPrefill({ phone: searchFromUrl.replace(/\D/g, "").slice(-10), name: nameFromUrl });
      setShowNewChat(true);
    }
  }, [convsLoaded, convs, searchFromUrl, nameFromUrl]);
  useEffect(() => { loadMetrics(); api.get("/admin/command-center/team").then(r => setTeam(r.data || {})); const t = setInterval(loadMetrics, 60000); return () => clearInterval(t); }, [loadMetrics]);

  // realtime: refetch on incoming
  useEffect(() => { if (liveConversation || liveMessage) loadConvs(); /* eslint-disable-next-line */ }, [liveConversation, liveMessage]);

  const active = useMemo(() => convs.find(c => c.id === activeId), [convs, activeId]);

  return (
    <div className="space-y-3" data-testid="command-center">
      <MetricsStrip metrics={metrics} onShowInternal={() => setShowInternal(true)} />

      <div className="flex flex-wrap gap-1 text-xs">
        {["", "today", "week"].map(w => (
          <button key={w || "all"} onClick={() => setWindowFilter(w)} data-testid={`cc-window-${w || "all"}`}
            className={`px-3 py-1 rounded-full font-bold ${windowFilter === w ? "bg-[#1a3c5e] text-white" : "bg-white border border-gray-300 text-gray-600"}`}>
            {w === "" ? "All time" : w === "today" ? "Today" : "This Week"}
          </button>
        ))}
        <div className="w-full sm:w-auto sm:ml-auto flex items-center flex-wrap gap-1.5">
          <button
            onClick={() => setShowNewChat(true)}
            data-testid="cc-new-chat-btn"
            className="bg-[#F7941D] hover:bg-[#e87f00] text-white font-bold px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1"
            title="Start a WhatsApp chat with any custom phone number"
          >
            <UserPlus className="w-3.5 h-3.5" /> New Chat
          </button>
          <button
            onClick={() => setShowWebhookDebug(true)}
            data-testid="cc-webhook-debug-btn"
            className="bg-white border border-gray-300 text-[#1a3c5e] font-bold px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1 hover:bg-gray-50"
            title="View incoming WhatsApp webhook hits — use to diagnose 'inbound not arriving'"
          >
            <Activity className="w-3.5 h-3.5" /> Webhook Hits
          </button>
          <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-full px-3 py-1 flex-1 min-w-[160px] sm:flex-initial">
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name / phone / project / message..." data-testid="cc-search"
              className="text-xs outline-none w-full sm:w-56" />
          </div>
        </div>
      </div>

      {/* A6: Sales / Support / All — switching channel resets category to "all" */}
      <div className="flex flex-wrap items-center gap-1.5">
        {CHANNELS.map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setChannel(key)} data-testid={`cc-channel-${key}`}
            className={`px-3.5 py-1.5 rounded-full text-xs flex items-center gap-1.5 font-bold transition ${channel === key ? "bg-[#F7941D] text-white" : "bg-white border-2 border-[#F7941D]/30 text-[#1a3c5e] hover:border-[#F7941D]"}`}>
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {categoriesForChannel(channel).map(({ key, label, Icon, badge }) => (
          <button key={key} onClick={() => setCategory(key)} data-testid={`cc-cat-${key}`}
            className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 font-medium transition ${category === key ? "bg-[#1a3c5e] text-white" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
            <Icon className="w-3.5 h-3.5" />
            {label}
            {badge && metrics[badge] > 0 && <span className="text-[10px] bg-[#F7941D] text-white px-1.5 rounded-full font-bold ml-0.5">{metrics[badge]}</span>}
          </button>
        ))}
      </div>

      {/* Mobile/iPhone: one pane visible at a time (list -> chat, intel as a drawer).
          Desktop (lg+): all 3 columns side by side, same as before. The old fixed
          320px_minmax(0,1fr)_320px grid had no breakpoints at all, so on a phone
          width it just squeezed all 3 columns into whatever space was left,
          overlapping/clipping the header buttons and message thread. */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)_300px] gap-3 h-[calc(100vh-280px)] min-h-[500px] lg:min-h-[600px]">
        {/* Conversation list */}
        <section className={`bg-white rounded-2xl border border-gray-200 overflow-y-auto min-h-0 ${active ? "hidden lg:block" : ""}`}>
          {convs.length === 0 && <div className="text-center py-10 text-gray-500 text-sm">No conversations in this view.</div>}
          {convs.map(c => (
            <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`cc-conv-${c.id}`}
              className={`w-full text-left px-3 py-3 border-b border-gray-100 hover:bg-gray-50 transition ${activeId === c.id ? "bg-[#FEF3E2]" : ""}`}>
              <div className="flex items-start gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1a3c5e] to-[#F7941D] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {(convDisplayName(c) || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-sm text-[#1a3c5e] truncate">{convDisplayName(c)}</span>
                    {c.lead?.score >= 7 && <Flame className="w-3.5 h-3.5 text-rose-500" title="Hot Lead" />}
                    {c.lead?.next_follow_up_at && new Date(c.lead.next_follow_up_at) <= new Date() && <Clock className="w-3.5 h-3.5 text-amber-500" title="Follow-up due" />}
                    {c.channel === "whatsapp" && <span className="text-[8px] bg-green-100 text-green-700 px-1 rounded font-bold">WA</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{convDisplaySubtitle(c)}</div>
                  <div className="text-xs text-gray-600 truncate mt-0.5">{c.last_message_preview || "—"}</div>
                  <div className="flex items-center gap-1 mt-1 text-[10px] flex-wrap">
                    {c.classification && <span className={`px-1.5 py-0.5 rounded border font-semibold ${CLASSIFICATION_COLOR[c.classification]}`}>{CLASSIFICATION_LABEL[c.classification]}</span>}
                    {c.lead?.pipeline_stage && <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{c.lead.pipeline_stage}</span>}
                    {c.project?.workflow_stage && <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{c.project.workflow_stage}</span>}
                    {c.ai_intent && <span className={`px-1.5 py-0.5 rounded font-semibold border ${INTENT_COLORS[c.ai_intent] || INTENT_COLORS.casual_query}`} title={c.ai_intent_reason || ""}>{c.ai_intent.replace("_", " ")}</span>}
                    {c.sla_breach && <span className="bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" /> SLA</span>}
                    {(c.unread_admin > 0) && <span className="bg-[#F7941D] text-white px-1.5 py-0.5 rounded font-bold ml-auto">{c.unread_admin}</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </section>

        {/* Chat pane — hidden on mobile until a conversation is picked (list shows instead) */}
        <section className={`bg-white rounded-2xl border border-gray-200 flex-col overflow-hidden min-h-0 min-w-0 ${active ? "flex" : "hidden lg:flex"}`}>
          {!active ? (
            <div className="m-auto text-gray-400 text-sm flex flex-col items-center gap-2 p-6 text-center">
              <MessageCircle className="w-10 h-10 text-gray-300" />
              <div>Select a conversation to view the chat thread.</div>
              <div className="text-xs text-gray-300">Live WhatsApp messages will appear here in real time.</div>
            </div>
          ) : (
            <ChatPane
              conv={active} liveMessage={liveMessage} liveTyping={liveTyping} liveAIControl={liveAIControl}
              sendTyping={sendTyping} team={team} onAssign={() => setShowAssign(true)} onReload={loadConvs}
              draftInjection={draftInjection}
              onBack={() => setActiveId(null)}
              onShowIntel={() => setShowIntelMobile(true)}
            />
          )}
        </section>

        {/* Intelligence panel — always-visible 3rd column on desktop */}
        <aside className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-y-auto min-h-0">
          {active ? <IntelPanel conv={active} team={team} onApplyDraft={injectDraft} /> : <div className="m-auto p-6 text-center text-sm text-gray-400">Conversation intelligence will appear here.</div>}
        </aside>
      </div>

      {/* Intelligence panel — mobile slide-over drawer, opened via the chat header's Info button */}
      {showIntelMobile && active && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end lg:hidden" onClick={() => setShowIntelMobile(false)} data-testid="cc-intel-mobile-overlay">
          <div className="bg-[#FEF3E2]/30 w-full max-w-sm h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-gray-200 sticky top-0 bg-white z-10">
              <span className="font-bold text-[#1a3c5e] text-sm">Conversation Intelligence</span>
              <button onClick={() => setShowIntelMobile(false)} data-testid="cc-intel-mobile-close"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <IntelPanel conv={active} team={team} onApplyDraft={injectDraft} />
          </div>
        </div>
      )}

      {showAssign && active && <AssignModal conv={active} team={team} onClose={() => setShowAssign(false)} onDone={() => { setShowAssign(false); loadConvs(); }} />}
      {showInternal && <InternalChannelsModal team={team} onClose={() => setShowInternal(false)} />}
      {showNewChat && (
        <NewChatModal
          initialPhone={newChatPrefill?.phone || ""}
          initialName={newChatPrefill?.name || ""}
          onClose={() => { setShowNewChat(false); setNewChatPrefill(null); }}
          onCreated={(convId) => { setShowNewChat(false); setNewChatPrefill(null); loadConvs(); setActiveId(convId); }}
        />
      )}
      {showWebhookDebug && <WebhookHitsModal onClose={() => setShowWebhookDebug(false)} />}
    </div>
  );
}

function MetricsStrip({ metrics, onShowInternal }) {
  const items = [
    { label: "Total", value: metrics.total_conversations || 0, Icon: InboxIcon, tone: "blue" },
    { label: "Unread", value: metrics.unread || 0, Icon: Mail, tone: "amber" },
    { label: "Hot Leads", value: metrics.hot_leads || 0, Icon: Flame, tone: "rose" },
    { label: "Follow-Ups Due", value: metrics.follow_ups_due || 0, Icon: Clock, tone: "orange" },
    { label: "Closed", value: metrics.closed || 0, Icon: CheckCircle, tone: "emerald" },
    { label: "Avg Response", value: metrics.avg_response_human || "—", Icon: Activity, tone: "purple" },
  ];
  const tones = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  return (
    <div className="flex items-stretch gap-2">
      <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {items.map(it => (
          <div key={it.label} className={`p-3 rounded-xl border ${tones[it.tone]}`} data-testid={`cc-metric-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <it.Icon className="w-4 h-4 mb-1" />
            <div className="text-[10px] uppercase font-bold opacity-70">{it.label}</div>
            <div className="text-xl font-bold">{it.value}</div>
          </div>
        ))}
      </div>
      <button onClick={onShowInternal} data-testid="cc-internal-btn" className="px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs font-bold text-[#1a3c5e] hover:bg-gray-50 inline-flex items-center gap-1">
        <AtSign className="w-4 h-4" /> Internal Team
      </button>
    </div>
  );
}

// Meta's 24h free-text window: open === recipient messaged us within the last 24h, so
// plain-text replies are allowed; once it closes, only an approved template can reach
// them. isOpen/closesAt come pre-computed from the conversation list API
// (wa_window_open / wa_window_closes_at, see command_center.py::_enrich_wa_window) --
// this component only re-renders the countdown locally, it doesn't recompute the gate.
function WaWindowBadge({ closesAt, isOpen }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  if (isOpen === null || isOpen === undefined || !closesAt) {
    return <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-bold" title="No inbound message on record yet — window status unknown">Window unknown</span>;
  }
  if (!isOpen) {
    return <span className="text-[9px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold" title="24h window closed — free-form text will be rejected by Meta; use a Template">Window closed</span>;
  }
  const msLeft = new Date(closesAt).getTime() - Date.now();
  const hLeft = Math.max(0, Math.floor(msLeft / 3600000));
  const mLeft = Math.max(0, Math.floor((msLeft % 3600000) / 60000));
  return (
    <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold" title="Free-form text allowed until this closes, then only a Template will go through">
      Window open · {hLeft}h {mLeft}m left
    </span>
  );
}

function ChatPane({ conv, liveMessage, liveTyping, liveAIControl, sendTyping, team, onAssign, onReload, draftInjection, onBack, onShowIntel }) {
  const [waDiag, setWaDiag] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showMktCard, setShowMktCard] = useState(false);
  React.useEffect(() => {
    if (conv?.channel === "whatsapp") {
      api.get("/admin/whatsapp/diagnostics").then(r => setWaDiag(r.data)).catch(() => setWaDiag(null));
    } else {
      setWaDiag(null);
    }
  }, [conv?.channel]);

  const close = async () => {
    if (!window.confirm("Close this conversation?")) return;
    try { await api.post(`/admin/inbox/${conv.id}/close`); toast.success("Closed"); onReload?.(); }
    catch { toast.error("Failed"); }
  };

  const header = (
    <>
      <div className="px-3 sm:px-4 py-3 border-b border-black/10 bg-[#f0f2f5] flex items-center gap-2 flex-wrap">
        {/* Mobile only: back to the conversation list instead of showing 3 squeezed columns */}
        <button onClick={onBack} data-testid="cc-back-btn" className="lg:hidden shrink-0 p-1.5 -ml-1 rounded-lg hover:bg-black/5 text-[#1a3c5e]" title="Back to conversations">
          <ArrowLeft className="w-4.5 h-4.5" />
        </button>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1a3c5e] to-[#F7941D] text-white flex items-center justify-center font-bold shrink-0">{(convDisplayName(conv) || "?")[0].toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#1a3c5e] truncate flex items-center gap-1.5">
            {convDisplayName(conv)}
            {conv.channel === "whatsapp" && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">WhatsApp</span>}
            {conv.channel === "whatsapp" && <WaWindowBadge closesAt={conv.wa_window_closes_at} isOpen={conv.wa_window_open} />}
            {conv.classification && <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${CLASSIFICATION_COLOR[conv.classification]}`} data-testid="cc-header-classification">{CLASSIFICATION_LABEL[conv.classification]}</span>}
            {conv.lead?.score >= 7 && <Flame className="w-3.5 h-3.5 text-rose-500" title="Hot Lead" />}
            {conv.sla_breach && <span className="text-[9px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" /> SLA breach</span>}
          </div>
          <div className="text-xs text-gray-500 truncate">{convDisplaySubtitle(conv)} · {conv.lead?.pipeline_stage ? `Lead · ${conv.lead.pipeline_stage}` : (conv.project ? `Project · ${conv.project.workflow_stage}` : "—")}</div>
          {conv.classification_active_orders?.length > 0 && (
            <div className="flex items-center gap-1 mt-0.5 flex-wrap" data-testid="cc-header-linked-orders">
              {conv.classification_active_orders.map((o) => (
                <span key={o.id} className="text-[9px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded" title={o.deadline ? `Deadline: ${new Date(o.deadline).toLocaleDateString()}` : ""}>
                  #{o.id?.slice(0, 8)} · {o.service_name || o.status}
                </span>
              ))}
            </div>
          )}
        </div>
        <button onClick={onAssign} data-testid="cc-assign-btn" className="text-xs px-3 py-1.5 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-lg inline-flex items-center gap-1">
          <UserPlus className="w-3.5 h-3.5" /> Assign
        </button>
        <AIControlHeader conversationId={conv.id} onChange={onReload} liveAIControl={liveAIControl} />
        {conv.channel === "whatsapp" && (
          <button onClick={() => setShowTemplate(true)} data-testid="cc-template-btn" className="text-xs px-3 py-1.5 bg-[#25D366] hover:bg-[#1ea855] text-white font-bold rounded-lg inline-flex items-center gap-1" title="Send approved WhatsApp template (works outside 24h window)">
            Template
          </button>
        )}
        {conv.channel === "whatsapp" && (
          <button onClick={() => setShowMktCard(true)} data-testid="cc-marketing-card-btn" className="text-xs px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg inline-flex items-center gap-1" title="Send a one-to-one marketing card (portfolio / package / brochure / offer)">
            <Sparkles className="w-3.5 h-3.5" /> Card
          </button>
        )}
        <button onClick={close} data-testid="cc-close-btn" className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg">Close</button>
        {/* Mobile only: intelligence panel is a drawer here instead of a permanent 3rd column */}
        <button onClick={onShowIntel} data-testid="cc-show-intel-btn" className="lg:hidden text-xs px-3 py-1.5 bg-white border border-gray-300 text-[#1a3c5e] font-bold rounded-lg inline-flex items-center gap-1" title="Conversation intelligence">
          <Info className="w-3.5 h-3.5" /> Info
        </button>
      </div>
      {conv.channel === "whatsapp" && waDiag && !waDiag.overall_ok && (
        <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 text-xs text-rose-800 flex items-start gap-2" data-testid="wa-warn-banner">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-bold">WhatsApp Cloud API not fully configured — outbound replies will be stored but NOT delivered to the customer.</div>
            <div className="text-rose-700">
              {(waDiag.checks || []).filter(c => !c.ok).map(c => <div key={c.name}>• {c.name}: {c.detail}{c.fix ? ` — ${c.fix}` : ""}</div>)}
            </div>
          </div>
          <a href="/admin/whatsapp" className="text-rose-700 underline whitespace-nowrap">Fix in Settings →</a>
        </div>
      )}
      {/* R4: config can be perfectly fine while Meta itself is having a transient
          outage right now — that's a live signal (a growing retry backlog), not a
          static config problem, so it gets its own banner rather than folding into
          the config-check one above. */}
      {conv.channel === "whatsapp" && waDiag?.send_retry_queue?.likely_live_outage && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="wa-outage-banner">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <div className="flex-1">
            <span className="font-bold">{waDiag.send_retry_queue.pending} WhatsApp message(s) queued for retry</span> — Meta may be having a transient issue right now. Sends will auto-retry with backoff; nothing is lost.
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <MessageThread
        conversationId={conv.id}
        role="admin"
        apiBase={`/admin/inbox/${conv.id}`}
        canInternalNote={true}
        header={header}
        onUnreadChange={onReload}
        liveMessage={liveMessage}
        liveTyping={liveTyping}
        sendTyping={sendTyping}
        draftInjection={draftInjection}
        onMessageSent={({ final_text, draft_context }) => {
          if (!draft_context || !draft_context.draft) return;
          // Log the AI reply provenance (Hybrid Mode audit trail). Best-effort.
          api.post("/admin/ai/copilot/reply-log", {
            conversation_id: conv.id,
            draft: draft_context.draft,
            final_text,
            source: "ai_draft_approved",
            style: draft_context.style,
            ai_state_intent: draft_context.ai_state_intent,
            ai_state_confidence: draft_context.ai_state_confidence,
          }).catch(() => { /* non-fatal */ });
        }}
      />
      {showTemplate && (
        <TemplateChooser
          conversationId={conv.id}
          onClose={() => setShowTemplate(false)}
          onSent={onReload}
        />
      )}
      {showMktCard && (
        <OneToOneMarketingCard
          conversationId={conv.id}
          onClose={() => setShowMktCard(false)}
          onSent={onReload}
        />
      )}
    </>
  );
}

function IntelPanel({ conv, team, onApplyDraft }) {
  const [tab, setTab] = useState("info");
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [followUps, setFollowUps] = useState([]);

  const loadAll = useCallback(() => {
    api.get(`/admin/inbox/${conv.id}/ownership-history`).then(r => setHistory(r.data?.history || []));
    api.get(`/admin/inbox/${conv.id}/internal-notes`).then(r => setNotes(r.data || []));
    api.get(`/admin/command-center/conversations/${conv.id}/timeline`).then(r => setTimeline(r.data || [])).catch(() => {});
    api.get(`/admin/command-center/conversations/${conv.id}/follow-ups`).then(r => setFollowUps(r.data || [])).catch(() => {});
  }, [conv.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try { await api.post(`/admin/inbox/${conv.id}/internal-notes`, { body: noteBody }); setNoteBody(""); const r = await api.get(`/admin/inbox/${conv.id}/internal-notes`); setNotes(r.data || []); toast.success("Note added"); }
    catch { toast.error("Failed"); }
  };

  const sendFuNow = async (id) => {
    try { await api.post(`/sales/follow-ups/${id}/send-now`); toast.success("Sending..."); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const snoozeFu = async (id, hours) => {
    try { await api.post(`/sales/follow-ups/${id}/snooze`, { hours }); toast.success(`Snoozed +${hours}h`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doneFu = async (id) => {
    try { await api.post(`/sales/follow-ups/${id}/done`); toast.success("Marked done"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const lead = conv.lead || {};
  const project = conv.project || {};
  const TABS = [
    { k: "info", label: "Info" },
    { k: "timeline", label: "Timeline" },
    { k: "follow_ups", label: "Follow-ups" },
    { k: "history", label: "Owners" },
    { k: "notes", label: "Notes" },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {TABS.map(({ k, label }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cc-intel-tab-${k}`}
            className={`px-3 py-2 text-[11px] font-bold uppercase border-b-2 whitespace-nowrap ${tab === k ? "text-[#1a3c5e] border-[#F7941D]" : "text-gray-500 border-transparent"}`}>{label}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 text-sm">
        {tab === "info" && (
          <div className="space-y-3">
            <AISalesPanel conv={conv} onApplyDraft={onApplyDraft} />
            <AIInsightsSection conv={conv} onUpdated={loadAll} />
            <Section title="Lead Intelligence" Icon={Flame}>
              {lead.id ? (
                <div className="space-y-1 text-xs">
                  <Row label="Lead ID" value={lead.lead_id || lead.id.slice(0, 8)} mono />
                  <Row label="Name" value={lead.name || "—"} />
                  <Row label="Score" value={<span className={`font-bold ${lead.score >= 7 ? "text-rose-600" : lead.score >= 4 ? "text-amber-600" : "text-gray-600"}`}>{lead.score}/10</span>} />
                  <Row label="Category" value={lead.category || "—"} />
                  <Row label="Stage" value={lead.pipeline_stage || "—"} />
                  <Row label="Source" value={lead.source || "—"} />
                  <Row label="City" value={lead.city || "—"} />
                  <Row label="Last Follow-up" value={lead.last_follow_up_at ? lead.last_follow_up_at.slice(0, 10) : "—"} />
                  <Row label="Next Follow-up" value={lead.next_follow_up_at ? `${lead.next_follow_up_at.slice(0, 16).replace("T", " ")} (#${lead.next_follow_up_no})` : "—"} />
                </div>
              ) : <div className="text-xs text-gray-400">No lead linked. (auto-link runs as soon as the lead has a matching phone)</div>}
            </Section>
            <Section title="Project Intelligence" Icon={Briefcase}>
              {project.id ? (
                <div className="space-y-1 text-xs">
                  <Row label="Project ID" value={project.id.slice(0, 8).toUpperCase()} mono />
                  <Row label="Package" value={project.package_title || "—"} />
                  <Row label="Stage" value={project.workflow_stage} />
                  <Row label="Progress" value={`${project.workflow_progress || 0}%`} />
                  <Row label="Designer" value={project.designer?.name || "Unassigned"} />
                  <Row label="Pending Tasks" value={project.pending_tasks} />
                  <Row label="Pending Approvals" value={<span className={project.pending_approvals > 0 ? "text-rose-600 font-bold" : ""}>{project.pending_approvals}</span>} />
                  <Row label="Invoice" value={project.last_invoice ? `${project.last_invoice.invoice_no} (${project.last_invoice.status})` : "—"} />
                  <Row label="Balance Due" value={project.last_invoice?.balance_due ? `₹ ${project.last_invoice.balance_due.toLocaleString()}` : "—"} />
                </div>
              ) : <div className="text-xs text-gray-400">No project linked.</div>}
            </Section>
            <Section title="WhatsApp" Icon={PhoneCall}>
              <div className="space-y-1 text-xs">
                <Row label="Channel" value={conv.channel || "in_app"} />
                <Row label="Phone" value={conv.whatsapp_phone || conv.customer_phone || conv.customer_wa_id || "—"} mono />
                <Row label="Conv. type" value={conv.conversation_type || "—"} />
              </div>
            </Section>
          </div>
        )}
        {tab === "timeline" && (
          <div className="space-y-2" data-testid="cc-timeline-list">
            {timeline.length === 0 && <div className="text-xs text-gray-400">No events yet for this conversation.</div>}
            {timeline.map((e, i) => (
              <div key={i} className="text-xs border-l-2 border-[#1a3c5e] pl-2">
                <div className="font-mono font-bold text-[#1a3c5e]">{e.event_type}</div>
                <div className="text-gray-600">{e.actor?.name || e.actor_role || "system"} · {(e.created_at || "").slice(0, 16).replace("T", " ")}</div>
                {e.entity_type && <div className="text-gray-400">{e.entity_type} {(e.entity_id || "").slice(0, 8)}</div>}
              </div>
            ))}
          </div>
        )}
        {tab === "follow_ups" && (
          <div className="space-y-2" data-testid="cc-followups-list">
            {followUps.length === 0 && <div className="text-xs text-gray-400">{lead.id ? "No follow-ups scheduled." : "No lead linked — follow-ups appear once a lead is linked."}</div>}
            {followUps.map(f => (
              <div key={f.id} className={`p-2 border rounded-lg text-xs ${f.status === "pending" ? "border-amber-200 bg-amber-50" : f.status === "sent" ? "border-emerald-200 bg-emerald-50" : "border-gray-200 bg-gray-50"}`} data-testid={`cc-followup-${f.id}`}>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-[#1a3c5e]">#{f.attempt_no || "?"} · {f.status}</span>
                  <span className="text-gray-500">{(f.scheduled_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                {f.status === "pending" && (
                  <div className="flex gap-1 mt-1.5">
                    <button onClick={() => sendFuNow(f.id)} className="px-2 py-0.5 bg-[#F7941D] text-white rounded text-[10px] font-bold">Send now</button>
                    <button onClick={() => snoozeFu(f.id, 24)} className="px-2 py-0.5 bg-white border border-gray-300 rounded text-[10px]">+24h</button>
                    <button onClick={() => doneFu(f.id)} className="px-2 py-0.5 bg-emerald-600 text-white rounded text-[10px]">Done</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {tab === "history" && (
          <div className="space-y-2">
            {history.length === 0 && <div className="text-xs text-gray-400">No assignment history.</div>}
            {history.map((h, i) => (
              <div key={i} className="border-l-2 border-[#F7941D] pl-2 text-xs" data-testid={`cc-history-${i}`}>
                <div className="font-bold text-[#1a3c5e]">{h.action.replace(/_/g, " ")}</div>
                <div className="text-gray-600">by {h.by_user?.name || h.by_role} · {(h.at || "").slice(0, 16).replace("T", " ")}</div>
                {h.to_user && <div className="text-gray-500">→ {h.to_user.name}</div>}
              </div>
            ))}
          </div>
        )}
        {tab === "notes" && (
          <div className="space-y-2">
            {notes.length === 0 && <div className="text-xs text-gray-400 mb-2">No internal notes yet.</div>}
            {notes.map(n => (
              <div key={n.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs" data-testid={`cc-note-${n.id}`}>
                <div className="font-bold text-[#1a3c5e]">{n.sender?.name || n.sender_role} <span className="text-gray-500 font-normal">· {(n.created_at || "").slice(0, 16).replace("T", " ")}</span></div>
                <div className="mt-1 text-gray-800 whitespace-pre-wrap">{n.body}</div>
                {n.mentions?.length > 0 && <div className="text-[10px] text-[#F7941D] mt-1">@ {n.mentions.length} mentioned</div>}
              </div>
            ))}
            <div className="border-t border-gray-200 pt-2">
              <textarea rows={2} value={noteBody} onChange={e => setNoteBody(e.target.value)} data-testid="cc-note-input"
                placeholder="Add internal note (use @email to mention)..." className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5" />
              <button onClick={addNote} disabled={!noteBody.trim()} data-testid="cc-note-add" className="mt-1 px-3 py-1 bg-[#1a3c5e] text-white text-xs font-bold rounded-lg">Add Note</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, Icon, children }) => (
  <div>
    <div className="text-[10px] font-bold uppercase text-gray-500 mb-2 inline-flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {title}</div>
    {children}
  </div>
);
const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-2"><span className="text-gray-500">{label}</span><span className={`${mono ? "font-mono" : ""} text-right`}>{value}</span></div>
);

function AssignModal({ conv, team, onClose, onDone }) {
  const [tab, setTab] = useState("designer");
  const [selected, setSelected] = useState("");
  const submit = async () => {
    try {
      const url = tab === "sales" ? `/admin/inbox/${conv.id}/assign-sales` : `/admin/inbox/${conv.id}/assign`;
      const body = tab === "sales" ? { sales_user_id: selected || null } : { designer_id: selected };
      await api.post(url, body);
      toast.success("Assigned");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const list = tab === "designer" ? team.designers : team.sales.concat(team.admins);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cc-assign-modal">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between"><h3 className="font-bold text-[#1a3c5e]">Assign Conversation</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="flex gap-1 border-b border-gray-200 px-4">
          <button onClick={() => { setTab("designer"); setSelected(""); }} data-testid="cc-assign-tab-designer" className={`px-3 py-2 text-xs font-bold border-b-2 ${tab === "designer" ? "text-[#1a3c5e] border-[#F7941D]" : "text-gray-500 border-transparent"}`}>Designer</button>
          <button onClick={() => { setTab("sales"); setSelected(""); }} data-testid="cc-assign-tab-sales" className={`px-3 py-2 text-xs font-bold border-b-2 ${tab === "sales" ? "text-[#1a3c5e] border-[#F7941D]" : "text-gray-500 border-transparent"}`}>Sales Executive</button>
        </div>
        <div className="p-4 max-h-[300px] overflow-y-auto">
          {list.length === 0 && <div className="text-sm text-gray-500 text-center py-4">No {tab}s available.</div>}
          {list.map(u => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded-lg cursor-pointer">
              <input type="radio" name="assignee" checked={selected === u.id} onChange={() => setSelected(u.id)} data-testid={`cc-assign-user-${u.id}`} />
              <span className="text-sm font-semibold">{u.name || u.email}</span>
              <span className="ml-auto text-xs text-gray-500">{u.role}</span>
            </label>
          ))}
        </div>
        <div className="border-t border-gray-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={!selected} data-testid="cc-assign-submit" className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-40 text-white text-sm font-bold rounded-xl">Assign</button>
        </div>
      </div>
    </div>
  );
}

function InternalChannelsModal({ team, onClose }) {
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [startWith, setStartWith] = useState("");

  const load = () => api.get("/admin/command-center/internal-conv").then(r => setChannels(r.data || []));
  useEffect(() => { load(); }, []);

  const start = async (uid) => {
    try { const r = await api.post("/admin/command-center/internal-conv", { with_user_id: uid }); load(); setActiveId(r.data.id); setStartWith(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const loadMessages = (id) => api.get(`/admin/command-center/internal-conv/${id}/messages`).then(r => setMessages(r.data || []));
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId]);

  const send = async () => {
    if (!body.trim() || !activeId) return;
    try { await api.post(`/admin/command-center/internal-conv/${activeId}/messages`, { body }); setBody(""); loadMessages(activeId); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const allTeam = [...team.admins, ...team.sales, ...team.designers];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cc-internal-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full h-[80vh] flex flex-col">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between"><h3 className="font-bold text-[#1a3c5e] inline-flex items-center gap-2"><AtSign className="w-5 h-5" /> Internal Team Channels</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="flex-1 grid grid-cols-[220px_1fr] overflow-hidden">
          <aside className="border-r border-gray-200 overflow-y-auto">
            <div className="p-2 border-b border-gray-100 bg-gray-50">
              <select onChange={e => { if (e.target.value) start(e.target.value); }} value={startWith} data-testid="cc-internal-start" className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5">
                <option value="">+ Start new chat with…</option>
                {allTeam.map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
              </select>
            </div>
            {channels.length === 0 && <div className="text-center py-6 text-xs text-gray-400">No channels yet.</div>}
            {channels.map(c => (
              <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`cc-internal-ch-${c.id}`}
                className={`w-full text-left px-3 py-2 border-b border-gray-100 text-sm ${activeId === c.id ? "bg-[#FEF3E2]" : "hover:bg-gray-50"}`}>
                <div className="font-semibold truncate">{c.title}</div>
                <div className="text-xs text-gray-500 truncate">{c.last_message_preview || "—"}</div>
              </button>
            ))}
          </aside>
          <section className="flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-3 bg-gray-50 space-y-2">
              {messages.length === 0 ? <div className="text-center text-gray-400 text-sm">No messages.</div> : messages.map(m => (
                <div key={m.id} className="bg-white rounded-lg border border-gray-200 p-2 text-sm" data-testid={`cc-internal-msg-${m.id}`}>
                  <div className="text-[10px] uppercase font-bold text-[#F7941D]">{m.sender?.name || m.sender_role}</div>
                  <div className="text-gray-800 whitespace-pre-wrap">{m.body}</div>
                  <div className="text-[10px] text-gray-400 mt-1">{(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
            {activeId && (
              <div className="border-t border-gray-200 p-2 flex gap-2">
                <input value={body} onChange={e => setBody(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} data-testid="cc-internal-input" placeholder="Type message…" className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm" />
                <button onClick={send} disabled={!body.trim()} data-testid="cc-internal-send" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white text-sm font-bold rounded-xl"><Send className="w-4 h-4" /></button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

const INTENT_COLORS = {
  hot_lead: "bg-rose-100 text-rose-800 border-rose-300",
  wants_price: "bg-amber-100 text-amber-800 border-amber-300",
  wants_revision: "bg-orange-100 text-orange-800 border-orange-300",
  wants_status: "bg-blue-100 text-blue-800 border-blue-300",
  complaint: "bg-red-100 text-red-800 border-red-300",
  scheduling: "bg-emerald-100 text-emerald-800 border-emerald-300",
  casual_query: "bg-gray-100 text-gray-700 border-gray-300",
  spam: "bg-slate-200 text-slate-600 border-slate-400",
};

function AIInsightsSection({ conv, onUpdated }) {
  const [busy, setBusy] = useState(null); // 'summarize' | 'classify'
  const [summary, setSummary] = useState(conv?.ai_summary || "");
  const [summaryAt, setSummaryAt] = useState(conv?.ai_summary_at || "");
  const [intent, setIntent] = useState(conv?.ai_intent || "");
  const [intentReason, setIntentReason] = useState(conv?.ai_intent_reason || "");
  const [intentConfidence, setIntentConfidence] = useState(conv?.ai_intent_confidence || 0);

  useEffect(() => {
    setSummary(conv?.ai_summary || "");
    setSummaryAt(conv?.ai_summary_at || "");
    setIntent(conv?.ai_intent || "");
    setIntentReason(conv?.ai_intent_reason || "");
    setIntentConfidence(conv?.ai_intent_confidence || 0);
  }, [conv?.id, conv?.ai_summary, conv?.ai_intent]);

  const runSummary = async () => {
    setBusy("summarize");
    try {
      const r = await api.post("/admin/ai/summarize", { conversation_id: conv.id, style: "short" });
      setSummary(r.data.summary || "");
      setSummaryAt(r.data.generated_at);
      onUpdated && onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
    finally { setBusy(null); }
  };

  const runIntent = async () => {
    setBusy("classify");
    try {
      const r = await api.post("/admin/ai/classify-intent", { conversation_id: conv.id });
      setIntent(r.data.intent || "");
      setIntentConfidence(r.data.confidence || 0);
      setIntentReason(r.data.reason || "");
      onUpdated && onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
    finally { setBusy(null); }
  };

  return (
    <Section title="AI Insights" Icon={Sparkles}>
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={runSummary} disabled={busy === "summarize"} data-testid="cc-ai-summarize-btn"
            className="bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 disabled:opacity-50">
            {busy === "summarize" ? "Summarising…" : "Summarize chat"}
          </button>
          <button onClick={runIntent} disabled={busy === "classify"} data-testid="cc-ai-intent-btn"
            className="bg-purple-600 hover:bg-purple-700 text-white text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 disabled:opacity-50">
            {busy === "classify" ? "Tagging…" : "Tag intent"}
          </button>
        </div>
        {intent && (
          <div data-testid="cc-ai-intent-display">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${INTENT_COLORS[intent] || INTENT_COLORS.casual_query}`}>
                {intent.replace("_", " ")}
              </span>
              <span className="text-[10px] text-gray-500">{Math.round((intentConfidence || 0) * 100)}% confidence</span>
            </div>
            {intentReason && <div className="text-[11px] text-gray-600 mt-1 italic">{intentReason}</div>}
          </div>
        )}
        {summary && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-gray-700" data-testid="cc-ai-summary-display">
            {summary}
            {summaryAt && <div className="text-[10px] text-gray-400 mt-1">Generated {summaryAt.slice(0, 16).replace("T", " ")} UTC</div>}
          </div>
        )}
        {!intent && !summary && (
          <div className="text-[11px] text-gray-400">Click the buttons above to let Gemini Flash analyse this chat.</div>
        )}
      </div>
    </Section>
  );
}



// ============== NEW CHAT MODAL — start a WhatsApp conversation with a custom number ==============
function NewChatModal({ onClose, onCreated, initialPhone = "", initialName = "" }) {
  const [phone, setPhone] = useState(initialPhone);
  const [name, setName] = useState(initialName);
  const [mode, setMode] = useState("template"); // 'template' or 'free_form'
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [templateParams, setTemplateParams] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/whatsapp/templates").then(r => {
      const items = (r.data?.templates || r.data || []).filter(t => (t.status || "APPROVED") === "APPROVED");
      setTemplates(items);
      // Auto-select the first approved template, or fall back to Meta's built-in hello_world
      if (items.length > 0) {
        setTemplateName(items[0].name);
      } else {
        setTemplateName("hello_world");
      }
    }).catch(() => {
      setTemplateName("hello_world");
    });
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const last10 = (phone || "").replace(/\D/g, "").slice(-10);
    if (last10.length !== 10) { toast.error("Indian mobile (10 digits) required"); return; }
    if (mode === "template" && !templateName) { toast.error("Select a template"); return; }
    if (mode === "free_form" && !body.trim()) { toast.error("Message body required"); return; }
    setBusy(true);
    try {
      const payload = {
        phone: `+91${last10}`,
        name: name.trim() || null,
        template_name: mode === "template" ? templateName : null,
        template_params: mode === "template"
          ? templateParams.split(",").map(s => s.trim()).filter(Boolean)
          : null,
        body: mode === "free_form" ? body.trim() : null,
      };
      const r = await api.post("/admin/whatsapp/start-chat", payload);
      setResult(r.data);
      if (r.data?.sent) {
        toast.success("Message sent via Meta. Conversation created.");
      } else if (!r.data?.enabled) {
        toast.warning("Conversation created — but Meta credentials NOT configured. No WhatsApp message was sent.");
      } else {
        toast.error("Conversation created, but Meta rejected the send. See response details below.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start chat");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="cc-new-chat-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-bold text-[#1a3c5e]">Start New WhatsApp Chat</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="cc-new-chat-close">×</button>
        </div>
        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 mb-1">Phone *</label>
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
                <span className="text-sm text-gray-500">+91</span>
                <input
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit mobile"
                  className="flex-1 outline-none text-sm"
                  data-testid="cc-new-chat-phone"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 mb-1">Lead name (optional)</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Customer name (helps in CRM)" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="cc-new-chat-name" />
            </div>

            <div className="flex gap-2 border-t pt-3">
              <button type="button" onClick={() => setMode("template")} data-testid="cc-mode-template" className={`flex-1 py-2 text-xs font-bold rounded-lg ${mode === "template" ? "bg-[#1a3c5e] text-white" : "bg-gray-100 text-gray-600"}`}>
                Template (recommended)
              </button>
              <button type="button" onClick={() => setMode("free_form")} data-testid="cc-mode-freeform" className={`flex-1 py-2 text-xs font-bold rounded-lg ${mode === "free_form" ? "bg-[#1a3c5e] text-white" : "bg-gray-100 text-gray-600"}`}>
                Free-form text
              </button>
            </div>

            {mode === "template" ? (
              <>
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-500 mb-1">Template *</label>
                  <select value={templateName} onChange={e => setTemplateName(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="cc-new-chat-template">
                    {templates.length === 0 && <option value="hello_world">hello_world (Meta default)</option>}
                    {templates.map(t => (
                      <option key={t.name} value={t.name}>{t.name} · {t.language}</option>
                    ))}
                  </select>
                  {templates.length === 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">No approved templates found via /api/admin/whatsapp/templates. Make sure WHATSAPP_BUSINESS_ACCOUNT_ID is set, or just use Meta&apos;s built-in <code className="bg-gray-100 px-1">hello_world</code>.</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-500 mb-1">Body parameters (comma-separated)</label>
                  <input value={templateParams} onChange={e => setTemplateParams(e.target.value)} placeholder="e.g. Suhel, ₹15,000" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="cc-new-chat-params" />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  Templates work even when the 24h customer-service window is closed — required for cold outreach.
                </div>
              </>
            ) : (
              <>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Hi, regarding your enquiry about the 3 BHK plan…" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="cc-new-chat-body" />
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  ⚠ Meta will reject free-form text if the recipient has not messaged you in the last 24 hours. For first-time outreach use a template.
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-bold">Cancel</button>
              <button type="submit" disabled={busy} className="flex-1 bg-[#F7941D] text-white rounded-lg py-2 text-sm font-bold disabled:opacity-50" data-testid="cc-new-chat-submit">
                {busy ? "Sending…" : "Send & Open Chat"}
              </button>
            </div>
          </form>
        ) : (
          <div data-testid="cc-new-chat-result" className="space-y-3">
            <div className={`rounded-lg p-3 text-sm ${result.sent ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {result.sent ? "✓ Message sent via Meta." : "⚠ Conversation created but no WhatsApp message went out."}
              <div className="text-xs mt-1">Phone: <strong>+{result.phone}</strong> · Channel: {result.channel_action} · Meta enabled: {String(result.enabled)}</div>
            </div>
            <details className="bg-gray-50 rounded-lg p-3 text-xs">
              <summary className="cursor-pointer font-bold text-gray-700">Meta raw response</summary>
              <pre className="mt-2 whitespace-pre-wrap text-[11px] text-gray-700">{JSON.stringify(result.meta_response, null, 2) || "null (Meta creds disabled)"}</pre>
            </details>
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-bold">Close</button>
              <button onClick={() => onCreated && onCreated(result.conversation_id)} className="flex-1 bg-[#1a3c5e] text-white rounded-lg py-2 text-sm font-bold" data-testid="cc-new-chat-open">
                Open Conversation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { convDisplayName, convDisplaySubtitle, WaWindowBadge, NewChatModal, CLASSIFICATION_LABEL, CLASSIFICATION_COLOR };

// ============== WEBHOOK HITS MODAL — diagnose 'inbound not arriving' ==============
function WebhookHitsModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/whatsapp/webhook-hits?limit=50").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="cc-webhook-debug-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-xl font-bold text-[#1a3c5e]">WhatsApp Webhook Hits</h3>
            <p className="text-xs text-gray-500">Live feed of every Meta webhook call. Auto-refresh every 5s.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className="p-2 hover:bg-gray-100 rounded-lg" title="Refresh now" data-testid="cc-webhook-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="cc-webhook-close">×</button>
          </div>
        </div>

        {data && (
          <>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs mb-3">
              <div className="font-bold text-[#1a3c5e] mb-1">Meta webhook config (paste into Meta dashboard)</div>
              <div className="font-mono text-[11px] break-all bg-white border rounded px-2 py-1">{data.webhook_url}</div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(data.env || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5">
                    <span className={`inline-block w-2 h-2 rounded-full ${v ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                    <span className="font-mono text-[10px]">{k}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2"><div className="text-[10px] uppercase font-bold text-emerald-700">Accepted (200, sig OK)</div><div className="text-xl font-bold text-emerald-700">{data.stats?.sig_ok_200 || 0}</div></div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-2"><div className="text-[10px] uppercase font-bold text-rose-700">Signature FAILED (401)</div><div className="text-xl font-bold text-rose-700">{data.stats?.sig_fail_401 || 0}</div></div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2"><div className="text-[10px] uppercase font-bold text-blue-700">Total recorded</div><div className="text-xl font-bold text-blue-700">{data.stats?.total || 0}</div></div>
            </div>

            <div className="text-xs text-gray-500 mb-2">
              {data.hits.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                  No webhook hits recorded yet.  This is the #1 reason inbound messages don&apos;t arrive — Meta is NOT calling your webhook. Check:
                  <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                    <li>Webhook URL configured in Meta App → WhatsApp → Configuration → Callback URL matches the URL above exactly.</li>
                    <li>Verify token in Meta matches <code className="bg-gray-200 px-1">WHATSAPP_VERIFY_TOKEN</code> in backend .env.</li>
                    <li>WhatsApp Business Account → subscribe to <strong>messages</strong> field.</li>
                    <li>Phone number permission: ensure the WABA phone is added to the test/production app.</li>
                    <li>Network / firewall: your VPS allows inbound POST from Meta IPs (no whitelist).</li>
                  </ol>
                </div>
              ) : <span>Last {data.hits.length} hits</span>}
            </div>

            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {data.hits.map(h => (
                <div key={h.id} data-testid={`cc-webhook-hit-${h.id}`}
                     className={`border rounded-lg p-2 text-xs ${h.signature_ok && h.http_status === 200 ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-bold">{h.source} · HTTP {h.http_status} · {h.signature_ok ? "sig OK" : (h.signature_present ? "sig FAIL" : "no sig")}</div>
                    <div className="text-[10px] text-gray-500">{(h.at || "").slice(0, 19).replace("T", " ")} UTC</div>
                  </div>
                  {h.error && <div className="text-rose-700 mt-1">⚠ {h.error}</div>}
                  {h.payload_summary && Object.keys(h.payload_summary).length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[10px] text-gray-600">payload summary</summary>
                      <pre className="text-[10px] mt-1 bg-white border rounded p-1 whitespace-pre-wrap">{JSON.stringify(h.payload_summary, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
